import { getPublicData } from "../../../../../services/publicPegasApi/MakePublicOrder";
import { reportColumnsConfig } from "../reportColumnsConfig";


export default async function PrintGeneralReports(payload = {}, invoiceData = {}, orderId = undefined, options = {}) {
  // payload may be:
  // - { meta: {...}, data: { key: [...] }, keys: [...] }  <-- from printGeneralReportUtils
  // - Array of sessions (backwards compatibility)
  // - single orderDetails object (backwards compatible)
  let publicData = {};
  try {
    publicData = await getPublicData();
    console.log("📞 بيانات الشركة المسترجعة:", publicData);
  } catch (err) {
    console.error("❌ فشل في جلب البيانات العامة:", err);
  }

  const logoUrl = publicData?.logo ? publicData.logo.trim().replace(/\s+/g, '') : "";

  // Helper to escape HTML
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    return String(v)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  };

  // Helper to get human readable arabic label
  const getLabel = (key) => {
    const map = {
      id: "معرف",
      type: "النوع",
      amount: "المبلغ",
      bank: "الخزنة",
      bank_to: "إلى خزنة",
      bank_from: "من خزنة",
      transaction_date: "تاريخ العملية",
      invoice: "فاتورة",
      user: "المستخدم",
      username: "اسم الدخول",
      name: "الاسم",
      total: "الإجمالي",
      price: "السعر",
      quantity: "الكمية",
      customer_name: "العميل",
      session_id: "معرف اليومية",
      session_date: "تاريخ اليومية",
      // add more mappings as needed
    };
    if (map[key]) return map[key];
    // fallback: prettify key (underscore/camel -> space) and return
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, c => c.toLowerCase());
  };

  // تحويل تاريخ ISO/Date إلى تنسيق 12 ساعة (مثال: 2026-01-01 3:05:09 م)
  const formatTo12Hour = (isoOrDate) => {
    const d = isoOrDate ? new Date(isoOrDate) : new Date();
    if (isNaN(d.getTime())) return esc(isoOrDate || "");
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const H = d.getHours();
    const ampm = H >= 12 ? "م" : "ص";
    const h12 = H % 12 === 0 ? 12 : H % 12;
    const min = String(d.getMinutes()).padStart(2, "0");
    const sec = String(d.getSeconds()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${h12}:${min}:${sec} ${ampm}`;
  };

  // If payload has .data => treat as general report package
  if (payload && typeof payload === "object" && payload.data && typeof payload.data === "object") {
    const meta = payload.meta || {};
    const dataObj = payload.data || {};
    const keys = Array.isArray(payload.keys) && payload.keys.length ? payload.keys : Object.keys(dataObj);

    const sectionsHtml = keys.map((key) => {
      const arr = Array.isArray(dataObj[key]) ? dataObj[key] : [];

      // حاول الحصول على تعريف الأعمدة من reportColumnsConfig[key]
      const cfg = reportColumnsConfig?.[key] || {};
      let headerDefs = [];
      if (cfg && (Array.isArray(cfg.columns) || Array.isArray(cfg.fields))) {
        const cols = cfg.columns || cfg.fields;
        headerDefs = cols.map(c => {
          if (typeof c === "string") {
            // عنوان ممكن موجود في cfg.titles أو cfg.labels
            const title = (cfg.titles && cfg.titles[c]) || (cfg.labels && cfg.labels[c]) || getLabel(c);
            return { field: c, title };
          }
          // كائن عمود: { field, title } أو { key, title }
          const field = c.field || c.key || c.id || c.name;
          const title = c.title || c.label || (cfg.titles && cfg.titles[field]) || getLabel(field);
          return { field, title };
        }).filter(Boolean);
      } else {
        // compute union of all keys across all items
        const headerSet = arr.reduce((set, item) => {
          if (item && typeof item === "object") {
            Object.keys(item).forEach(k => set.add(k));
          }
          return set;
        }, new Set());
        const headers = Array.from(headerSet);
        headerDefs = headers.map(h => ({ field: h, title: getLabel(h) }));
      }

      const headerHtml = headerDefs.length
        ? headerDefs.map(h => `<th style="border:1px solid #ddd;padding:6px">${esc(h.title)}</th>`).join("")
        : `<th style="border:1px solid #ddd;padding:6px">البيانات</th>`;

      const rowsHtml = arr.length
        ? arr.map((item, idx) => {
            if (headerDefs.length) {
              const cols = headerDefs.map(h => {
                let val = item?.[h.field];
                if (val && typeof val === "object") {
                  if (val.name) val = val.name;
                  else val = JSON.stringify(val);
                }
                return `<td style="border:1px solid #ddd;padding:6px">${esc(val)}</td>`;
              }).join("");
              return `<tr><td style="border:1px solid #ddd;padding:6px;text-align:center">${idx+1}</td>${cols}</tr>`;
            } else {
              return `<tr><td style="border:1px solid #ddd;padding:6px">${esc(JSON.stringify(item))}</td></tr>`;
            }
          }).join("")
        : `<tr><td colspan="${Math.max(1, headerDefs.length + 1)}" style="padding:6px;text-align:center">لا توجد سجلات</td></tr>`;

      return `
        <section style="border-bottom:1px dashed #ccc;padding:12px 0;margin-bottom:12px">
          <h3 style="margin:6px 0">${esc(cfg?.title || getLabel(key) || key)}</h3>
          <p style="margin:4px 0">عدد السجلات: ${arr.length}</p>
          <table style="width:100%;border-collapse:collapse;margin-top:6px">
            <thead>
              <tr>
                <th style="border:1px solid #ddd;padding:6px">م</th>
                ${headerHtml}
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </section>
      `;
    }).join("");

    const title = options?.title || "تقرير عام";
    const metaHtml = `
      <p>من تاريخ - الي: ${esc(meta.from || "")} — ${esc(meta.to || "")}</p>
      <p>تاريخ الإنشاء: ${esc(formatTo12Hour(meta.generatedAt || new Date()))}</p>
    `;

    const html = `<!doctype html>
      <html dir="rtl">
      <head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;direction:rtl;padding:20px}
        h1,h2,h3,h4{margin:6px 0}
        table{width:100%;border-collapse:collapse;margin-top:6px}
        th,td{border:1px solid #ddd;padding:6px;text-align:right}
        th{background:#f5f5f5}
        .logo{max-width:240px;max-height:80px;object-fit:contain;display:block;margin:0 auto 10px}
      </style>
      </head>
      <body>
        ${logoUrl ? `<div style="text-align:center;margin-bottom:10px"><img src="${esc(logoUrl)}" class="logo" onerror="this.style.display='none'"/></div>` : ""}
        <h1 style="text-align:center">${esc(publicData?.name || title)}</h1>
        ${metaHtml}
        ${sectionsHtml}
      </body>
      </html>`;
    return html;
  }

  // Backwards compatibility: if payload is array => sessions view (reuse old logic)
  if (Array.isArray(payload)) {
    const sessions = payload;
    const title = options?.title || "تقرير اليوميات";
    const rowsHtml = sessions.map((s) => {
      const grouped = s.grouped_transactions_by_shift || {};
      const totalTx = Object.values(grouped).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
      const shiftsHtml = Object.entries(grouped).map(([shiftName, txs]) => {
        const txRows = (txs || []).map((tx, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${esc(tx.type)}</td>
            <td>${esc(tx.amount)}</td>
            <td>${esc(tx.bank?.name)}</td>
            <td>${esc(tx.bank_to?.name)}</td>
            <td>${esc(tx.bank_from?.name)}</td>
            <td>${esc(tx.transaction_date)}</td>
            <td>${tx.invoice ? esc(`فاتورة ${tx.invoice.id}`) : "-"}</td>
          </tr>
        `).join("");
        return `
          <div style="margin-top:8px">
            <h4 style="margin:6px 0">الوردية: ${esc(shiftName)}</h4>
            <table style="width:100%;border-collapse:collapse;margin-bottom:6px">
              <thead>
                <tr>
                  <th style="border:1px solid #ddd;padding:6px">م</th>
                  <th style="border:1px solid #ddd;padding:6px">النوع</th>
                  <th style="border:1px solid #ddd;padding:6px">المبلغ</th>
                  <th style="border:1px solid #ddd;padding:6px">الخزنة</th>
                  <th style="border:1px solid #ddd;padding:6px">إلى خزنة</th>
                  <th style="border:1px solid #ddd;padding:6px">من خزنة</th>
                  <th style="border:1px solid #ddd;padding:6px">تاريخ العملية</th>
                  <th style="border:1px solid #ddd;padding:6px">فاتورة</th>
                </tr>
              </thead>
              <tbody>
                ${txRows || `<tr><td colspan="8" style="padding:6px;text-align:center">لا توجد عمليات</td></tr>`}
              </tbody>
            </table>
          </div>
        `;
      }).join("");

      return `
        <section style="border-bottom:1px dashed #ccc;padding:12px 0;margin-bottom:12px">
          <h3 style="margin:6px 0">اليومية: ${esc(s.session_id)} — ${esc(s.session_date)}</h3>
          <p style="margin:4px 0">المستخدم: ${esc(s.user?.name)} (${esc(s.user?.username)})</p>
          <p style="margin:4px 0">عدد العمليات: ${totalTx}</p>
          ${shiftsHtml}
        </section>
      `;
    }).join("");

    const html = `<!doctype html>
      <html dir="rtl">
      <head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;direction:rtl;padding:20px}
        h1,h2,h3,h4{margin:6px 0}
        table{width:100%;border-collapse:collapse;margin-top:6px}
        th,td{border:1px solid #ddd;padding:6px;text-align:right}
        th{background:#f5f5f5}
      </style>
      </head>
      <body>
        ${logoUrl ? `<div style="text-align:center;margin-bottom:10px"><img src="${esc(logoUrl)}" alt="logo" style="max-width:240px;max-height:80px;object-fit:contain" onerror="this.style.display='none'"/></div>` : ""}
        <h1 style="text-align:center">${esc(publicData?.name || "تقرير اليوميات")}</h1>
        <h2 style="text-align:center">إجمالي اليوميات: ${sessions.length}</h2>
        ${rowsHtml}
      </body>
      </html>
    `;
    return html;
  }

  // Fallback: single order printing (backwards compatible)
  const orderDetails = payload || {};
  const inv = invoiceData || {};
  const items = orderDetails.items || [];

  const subtotal = items.reduce(
    (acc, item) =>
      acc +
      parseFloat(item.price || item.unit_price || (item.total && item.quantity ? item.total / item.quantity : 0) || 0) *
        parseFloat(item.quantity || 0),
    0
  );

  const service = parseFloat(inv.service || 0);
  const tax = parseFloat(inv.tax || 0);
  const discount = parseFloat(inv.discount || 0);
  const total = subtotal + service + tax - discount;

  const footerText = (() => {
    const base = (inv.footer_text || "").trim();
    const note = (publicData?.invoice_note_printer || "").trim();
    const pieces = [];
    if (base) pieces.push(base);
    if (note) pieces.push(note);
    return pieces.join("<br/>");
  })();

  const address = publicData?.address || "";
  const phone = publicData?.phone || "";

  const orderPrintNumber = inv?.order_print_number || 1;

  return `
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <meta charset="UTF-8" />
      <style>
        @page { size: 80mm auto; margin: 0; }
        html, body { height: 100%; margin: 0; padding: 0; display: flex; justify-content: center; background: #fff; }
        .receipt { font-family: 'Cairo', sans-serif; width: 80mm; padding: 8px; margin: 0 auto; font-size: 13px; color: #000; direction: rtl; text-align: right; box-sizing: border-box; }
        h2, h3 { margin: 4px 0; text-align: center; }
        hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 3px 0; text-align: right; }
        th { border-bottom: 1px dashed #000; }
        .footer { text-align: center; margin-top: 10px; font-size: 12px; }
        .logo { width: 220px; height: 220px; object-fit: contain; display: block; margin: 0 auto 20px; transform: translateX(6mm); }
      </style>
    </head>
    <body>
      <div class="receipt">
        ${logoUrl ? `<img src="${logoUrl}" class="logo" alt="Logo" onerror="this.style.display='none'" />` : ""}
        <h2>${inv.header_text || publicData?.name || "تطبيق رستو كاش"}</h2>
        <h3>فاتورة رقم #${orderDetails.general_increment || orderId || "-"}</h3>
        <h3>رقم الفاتورة: ${orderId}</h3>
        <p><strong>عدد مرات الطباعة:</strong> ${orderPrintNumber}</p>
        <hr />
        <p>العميل: ${orderDetails.customer?.name || "بدون اسم"}</p>
        <p>الطاولة: ${orderDetails.table?.name || "غير محددة"}</p>
        <hr />
        <table>
          <thead>
            <tr>
              <th>الصنف</th>
              <th>السعر</th>
              <th>الكمية</th>
              <th>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            ${items
              .map((item) => {
                const price = parseFloat(item.price || item.unit_price || (item.total && item.quantity ? item.total / item.quantity : 0));
                return `
                  <tr>
                    <td>${item.product_name || "-"}</td>
                    <td>${(isNaN(price) ? 0 : price).toFixed(2)} ج</td>
                    <td>${item.quantity || 0}</td>
                    <td>${(parseFloat(item.total) || 0).toFixed(2)} ج</td>
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>
        <hr />
        <p>الإجمالي الفرعي: ${subtotal.toFixed(2)} ج</p>
        <p>الخدمة: ${service.toFixed(2)} ج</p>
        <p>الضريبة: ${tax.toFixed(2)} ج</p>
        <p>الخصم: ${discount.toFixed(2)} ج</p>
        <hr />
        <h3>الإجمالي الكلي: ${total.toFixed(2)} ج</h3>
        <hr />
        <div class="footer">
          ${footerText ? `<p>${footerText}</p>` : `<p>شكرًا لزيارتكم ❤️</p>`}
          ${address ? `<p>📍 العنوان: ${address}</p>` : ""}
          ${phone ? `<p>📞 الهاتف: ${phone}</p>` : ""}
          <p>تم بواسطة نظام ريستو كاش</p>
        </div>
      </div>
    </body>
    </html>
  `;
}



